import React, { useEffect, useState } from 'react';
import { Icon } from '../components/sp-primitives';
import { ConfirmModal } from '../components/sp-overlays';
import { PShell, SpWorkspaceLoading, useApp } from '../components/sp-shell';

const KEYS = [
  { k: 'page_size', label: 'Registos por página (GET)', hint: '25–200. Menor = mais pedidos, payloads mais leves.', min: 25, max: 200, step: 1 },
  { k: 'max_pages_per_task', label: 'Offsets por tarefa/requisição (páginas)', hint: '1–10. Quantas páginas cada tarefa processa antes de criar a próxima.', min: 1, max: 10, step: 1 },
];
const SIENGE_FIELD_GROUPS = [
  { key: 'document_identifications', label: 'document-identifications', title: 'TIPOS DE DOCUMENTOS' },
  { key: 'payment_categories', label: 'payment-categories', title: 'MÃO DE OBRA PRÓPRIA E ENCARGOS' },
];
const SIENGE_FIXED_FIELDS = [
  { key: 'indexId', label: 'indexId', subtitle: 'Id do indexador das contas', kind: 'integer' },
  { key: 'installmentsNumber', label: 'installmentsNumber', subtitle: 'Quantidade de parcelas', kind: 'integer' },
  { key: 'discount', label: 'discount', subtitle: 'Valor de desconto', kind: 'decimal' },
  { key: 'percentage', label: 'percentage', subtitle: 'Percentual apropriado do centro de custo', kind: 'decimal' },
  { key: 'costEstimationSheetId', label: 'costEstimationSheetId', subtitle: 'Código do item de orçamento', kind: 'costCode' },
];

function formatDecimalOne(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.0';
  return n.toFixed(1);
}

function formatCostCode(value) {
  const s = String(value ?? '').replace(/\D/g, '');
  if (!s) return '';
  return Number(s).toLocaleString('pt-BR');
}

const LOGIN_LAYOUT_FIELDS = [
  { key: 'wm_brand',    label: 'sp-wm-brand',            hint: 'Texto principal da marca no topo (ex: SIENGE)' },
  { key: 'wm_accent',   label: 'sp-wm-accent',           hint: 'Texto de destaque da marca no topo (ex: PAY)' },
  { key: 'hero_kicker', label: 'sp-login__hero-kicker',  hint: 'Linha pequena acima do título' },
  { key: 'hero_title',  label: 'sp-login__hero-title',   hint: 'Título principal — use \\n para quebra de linha', textarea: true },
  { key: 'hero_accent', label: 'sp-login__hero-accent',  hint: 'Trecho em destaque dentro do título' },
  { key: 'hero_lead',   label: 'sp-login__hero-lead',    hint: 'Parágrafo de descrição abaixo do título', textarea: true },
  { key: 'hero_footer', label: 'sp-login__hero-footer',  hint: 'Rodapé da área hero (ex: © Empresa · 2026)' },
];

const MENU_LAYOUT_FIELDS = [
  { key: 'menu_wm_brand',  label: 'sp-wm-brand',                  hint: 'Texto principal da marca no menu lateral (ex: SIENGE)' },
  { key: 'menu_wm_accent', label: 'sp-wm-accent',                  hint: 'Texto de destaque da marca no menu lateral (ex: PAY)' },
  { key: 'menu_brand_sub', label: 'sp-shell__aside-brand-sub',     hint: 'Subtítulo abaixo da marca no aside (ex: by FullTec)' },
];

function getTabFromParams(params) {
  if (params?.tab === 'debug') return 'debug';
  if (params?.tab === 'backend') return 'backend';
  if (params?.tab === 'sienge-fields') return 'sienge-fields';
  if (params?.tab === 'layout') return 'layout';
  if (params?.tab === 'dev') return 'dev';
  return 'api';
}

export function PSettings() {
  const app = useApp();
  const canEdit = true;
  const isDevModeAllowed = Number(app.currentUser?.id || 0) === 1;
  const [activeTab, setActiveTab] = useState(getTabFromParams(app.params));
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [fileDefaults, setFileDefaults] = useState(null);
  const [stored, setStored] = useState(null);
  const [form, setForm] = useState(null);
  const [fieldsDbDefaults, setFieldsDbDefaults] = useState(null);
  const [fieldsStored, setFieldsStored] = useState(null);
  const [fieldsForm, setFieldsForm] = useState({
    document_identifications: [],
    payment_categories: [],
    fixed_fields: {
      indexId: 0,
      installmentsNumber: 1,
      discount: 0.0,
      percentage: 0.0,
      costEstimationSheetId: '1000000001',
    },
  });
  const [saving, setSaving] = useState(false);
  const [devResetPreview, setDevResetPreview] = useState(null);
  const [devPreviewLoading, setDevPreviewLoading] = useState(false);
  const [paymentsResetPreview, setPaymentsResetPreview] = useState(null);
  const [paymentsPreviewLoading, setPaymentsPreviewLoading] = useState(false);
  const [paymentsResetSaving, setPaymentsResetSaving] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetModalType, setResetModalType] = useState(null); // 'payments' | 'system'
  const [resetModalPassword, setResetModalPassword] = useState('');
  const [resetModalError, setResetModalError] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [endpointCompanyId, setEndpointCompanyId] = useState('');
  const [layoutForm, setLayoutForm] = useState({ wm_brand: '', wm_accent: '', hero_kicker: '', hero_title: '', hero_accent: '', hero_lead: '', hero_footer: '', menu_wm_brand: '', menu_wm_accent: '', menu_brand_sub: '' });
  const [prefSyncForm, setPrefSyncForm] = useState({ batch_size: 10, filter: 'employees' });
  const [prefSyncStored, setPrefSyncStored] = useState(null);
  const [prefSyncSaving, setPrefSyncSaving] = useState(false);
  const [layoutSaving, setLayoutSaving] = useState(false);

  useEffect(() => {
    if (!isDevModeAllowed || !app.authToken) return;
    app.fetchLoginLayout().then((data) => {
      if (data) setLayoutForm((f) => ({ ...f, ...data }));
    }).catch(() => {});
  }, [isDevModeAllowed, app.authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!app.authToken) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const [data, fieldsData, prefData] = await Promise.all([
          app.fetchSiengeCreditorSyncSettings(),
          app.fetchSiengeFieldsOptions(),
          app.fetchCreditorPrefSync(),
        ]);
        if (cancelled) return;
        setPrefSyncStored(prefData?.stored ?? null);
        setPrefSyncForm({ batch_size: Number(prefData?.effective?.batch_size ?? 10), filter: prefData?.effective?.filter ?? 'employees' });
        setFileDefaults(data?.file_defaults || null);
        setStored(data?.stored ?? null);
        setForm(data?.effective || null);
        setFieldsDbDefaults(fieldsData?.db_defaults || null);
        setFieldsStored(fieldsData?.stored ?? null);
        setFieldsForm({
          document_identifications: Array.isArray(fieldsData?.effective?.document_identifications) ? fieldsData.effective.document_identifications : [],
          payment_categories: Array.isArray(fieldsData?.effective?.payment_categories) ? fieldsData.effective.payment_categories : [],
          fixed_fields: {
            indexId: Number(fieldsData?.effective?.fixed_fields?.indexId ?? 0),
            installmentsNumber: Number(fieldsData?.effective?.fixed_fields?.installmentsNumber ?? 1),
            discount: formatDecimalOne(fieldsData?.effective?.fixed_fields?.discount ?? 0.0),
            percentage: formatDecimalOne(fieldsData?.effective?.fixed_fields?.percentage ?? 0.0),
            costEstimationSheetId: String(fieldsData?.effective?.fixed_fields?.costEstimationSheetId ?? '1000000001'),
          },
        });
      } catch (e) {
        if (!cancelled) setErr(e.message || 'Falha ao carregar configurações');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [app.authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const nextTab = getTabFromParams(app.params);
    if ((nextTab === 'dev' || nextTab === 'backend' || nextTab === 'debug') && !isDevModeAllowed) {
      setActiveTab('api');
      return;
    }
    setActiveTab(nextTab);
  }, [app.params?.tab, isDevModeAllowed]);

  const onChange = (k, v) => {
    const n = v === '' ? '' : Number(v);
    setForm((f) => (f ? { ...f, [k]: n } : f));
  };

  const save = async () => {
    if (!canEdit || !form) return;
    for (const { k, min, max, label } of KEYS) {
      const v = form[k];
      if (v === '' || v === undefined || v === null) continue;
      const n = Number(v);
      if (Number.isNaN(n) || n < min || n > max) {
        app.toast('error', 'Configurações', `${label}: valor deve estar entre ${min} e ${max}.`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {};
      KEYS.forEach(({ k }) => {
        const v = form[k];
        if (v !== '' && v !== undefined && v !== null && !Number.isNaN(Number(v))) payload[k] = Number(v);
      });
      const data = await app.updateSiengeCreditorSyncSettings(payload);
      setStored(data?.stored ?? null);
      setForm(data?.effective || form);
      app.toast('success', 'Configurações', data?.message || 'Gravado.');
    } catch (e) {
      app.toast('error', 'Configurações', e.message || 'Falha ao gravar.');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!canEdit) return;
    setConfirmModal({
      title: 'Repor defaults',
      message: 'Remover valores gravados na base e voltar aos defaults do servidor (.env / config)?',
      confirmLabel: 'Repor',
      onConfirm: async () => {
        setSaving(true);
        try {
          const data = await app.resetSiengeCreditorSyncSettings();
          setStored(data?.stored ?? null);
          setForm(data?.effective || null);
          app.toast('success', 'Configurações', data?.message || 'Reposto.');
        } catch (e) {
          app.toast('error', 'Configurações', e.message || 'Falha ao repor.');
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const saveDebug = () => {
    const persist = async () => {
      try {
        const res = await app.updateDebugSetting(app.debugEnabled === 'S' ? 'S' : 'N');
        const next = res?.debug_enabled === 'S' ? 'S' : 'N';
        app.setDebugEnabled(next);
        app.toast('success', 'Configurações', `Debug ${next === 'S' ? 'ativado' : 'desativado'}.`);
      } catch (e) {
        app.toast('error', 'Configurações', e.message || 'Falha ao gravar DEBUG.');
      }
    };
    persist();
  };

  const updateFieldRow = (groupKey, idx, patch) => {
    setFieldsForm((curr) => ({
      ...curr,
      [groupKey]: (curr[groupKey] || []).map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    }));
  };

  const addFieldRow = (groupKey) => {
    setFieldsForm((curr) => ({
      ...curr,
      [groupKey]: [...(curr[groupKey] || []), { value: '', name: '' }],
    }));
  };

  const removeFieldRow = (groupKey, idx) => {
    setConfirmModal({
      title: 'Excluir campo',
      message: 'Criar ou excluir estes valores pode danificar a integração com o Sienge. Tem certeza que deseja excluir?',
      confirmLabel: 'Excluir',
      onConfirm: () => {
        setFieldsForm((curr) => ({
          ...curr,
          [groupKey]: (curr[groupKey] || []).filter((_, i) => i !== idx),
        }));
      },
    });
  };

  const saveSiengeFields = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const payload = {};
      SIENGE_FIELD_GROUPS.forEach(({ key }) => {
        payload[key] = (fieldsForm[key] || [])
          .map((row) => ({ value: String(row?.value || '').trim(), name: String(row?.name || '').trim() }))
          .filter((row) => row.value !== '' && row.name !== '');
      });
      payload.fixed_fields = {
        indexId: Number(fieldsForm?.fixed_fields?.indexId ?? 0),
        installmentsNumber: Number(fieldsForm?.fixed_fields?.installmentsNumber ?? 0),
        discount: Number(String(fieldsForm?.fixed_fields?.discount ?? 0.0).replace(',', '.')),
        percentage: Number(String(fieldsForm?.fixed_fields?.percentage ?? 0.0).replace(',', '.')),
        costEstimationSheetId: String(fieldsForm?.fixed_fields?.costEstimationSheetId ?? '1000000001').replace(/\D/g, ''),
      };
      const data = await app.updateSiengeFieldsOptions(payload);
      setFieldsStored(data?.stored ?? null);
      setFieldsForm({
        document_identifications: Array.isArray(data?.effective?.document_identifications) ? data.effective.document_identifications : [],
        payment_categories: Array.isArray(data?.effective?.payment_categories) ? data.effective.payment_categories : [],
        fixed_fields: {
          indexId: Number(data?.effective?.fixed_fields?.indexId ?? 0),
          installmentsNumber: Number(data?.effective?.fixed_fields?.installmentsNumber ?? 1),
          discount: formatDecimalOne(data?.effective?.fixed_fields?.discount ?? 0.0),
          percentage: formatDecimalOne(data?.effective?.fixed_fields?.percentage ?? 0.0),
        },
      });
      app.toast('success', 'Campos do Sienge', data?.message || 'Gravado.');
    } catch (e) {
      app.toast('error', 'Campos do Sienge', e.message || 'Falha ao gravar.');
    } finally {
      setSaving(false);
    }
  };

  const resetSiengeFields = async () => {
    if (!canEdit) return;
    setConfirmModal({
      title: 'Repor campos',
      message: 'Repor campos do Sienge para o padrão guardado na base de dados?',
      confirmLabel: 'Repor',
      onConfirm: async () => {
        setSaving(true);
        try {
          const data = await app.resetSiengeFieldsOptions();
          setFieldsStored(data?.stored ?? null);
      setFieldsForm({
        document_identifications: Array.isArray(data?.effective?.document_identifications) ? data.effective.document_identifications : [],
        payment_categories: Array.isArray(data?.effective?.payment_categories) ? data.effective.payment_categories : [],
        fixed_fields: {
          indexId: Number(data?.effective?.fixed_fields?.indexId ?? 0),
          installmentsNumber: Number(data?.effective?.fixed_fields?.installmentsNumber ?? 1),
          discount: formatDecimalOne(data?.effective?.fixed_fields?.discount ?? 0.0),
          percentage: formatDecimalOne(data?.effective?.fixed_fields?.percentage ?? 0.0),
        },
      });
          app.toast('success', 'Campos do Sienge', data?.message || 'Reposto.');
        } catch (e) {
          app.toast('error', 'Campos do Sienge', e.message || 'Falha ao repor.');
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const savePrefSync = async () => {
    const v = Number(prefSyncForm.batch_size);
    if (!Number.isFinite(v) || v < 1 || v > 20) {
      app.toast('error', 'Preferências', 'Número de credores por tarefa deve ser entre 1 e 20.');
      return;
    }
    setPrefSyncSaving(true);
    try {
      const data = await app.updateCreditorPrefSync({ batch_size: v, filter: prefSyncForm.filter });
      setPrefSyncStored(data?.effective ?? null);
      app.toast('success', 'Preferências', 'Configuração gravada.');
    } catch (e) {
      app.toast('error', 'Preferências', e.message || 'Falha ao gravar.');
    } finally {
      setPrefSyncSaving(false);
    }
  };

  const saveLayout = async () => {
    setLayoutSaving(true);
    try {
      const data = await app.updateLoginLayout(layoutForm);
      setLayoutForm((f) => ({ ...f, ...data }));
      app.toast('success', 'Layout', 'Textos da tela de login atualizados.');
    } catch (e) {
      app.toast('error', 'Layout', e.message || 'Falha ao gravar.');
    } finally {
      setLayoutSaving(false);
    }
  };

  const loadDevResetPreview = async () => {
    setDevPreviewLoading(true);
    try {
      const data = await app.fetchDevelopmentResetPreview();
      setDevResetPreview(data?.preview || null);
    } catch (e) {
      app.toast('error', 'Modo Dev.', e.message || 'Falha ao carregar preview do reset.');
      setDevResetPreview(null);
    } finally {
      setDevPreviewLoading(false);
    }
  };

  const openResetModal = (type) => {
    setResetModalType(type);
    setResetModalPassword('');
    setResetModalError('');
    setResetModalOpen(true);
  };

  const confirmReset = async () => {
    if (!resetModalPassword) {
      setResetModalError('Informe a senha.');
      return;
    }
    setResetModalError('');
    if (resetModalType === 'payments') {
      setPaymentsResetSaving(true);
    } else {
      setSaving(true);
    }
    setResetModalOpen(false);
    try {
      const fn = resetModalType === 'payments' ? app.resetPayments : app.resetDevelopmentSystem;
      const data = await fn(resetModalPassword);
      const label = resetModalType === 'payments' ? 'Reset os Pagamentos' : 'Reset o Sistema';
      app.toast('success', label, data?.message || 'Reset concluído.');
      if (resetModalType === 'payments') {
        setPaymentsResetPreview(data?.deleted || null);
        app.navigate('dashboard');
      } else {
        setDevResetPreview(data?.deleted || null);
        await app.syncFromApi(undefined, 'full');
        app.navigate('companies');
      }
    } catch (e) {
      const label = resetModalType === 'payments' ? 'Reset os Pagamentos' : 'Reset o Sistema';
      app.toast('error', label, e.message || 'Falha ao executar reset.');
    } finally {
      setPaymentsResetSaving(false);
      setSaving(false);
    }
  };

  const loadPaymentsResetPreview = async () => {
    setPaymentsPreviewLoading(true);
    try {
      const data = await app.fetchPaymentResetPreview();
      setPaymentsResetPreview(data?.preview || null);
    } catch (e) {
      app.toast('error', 'Modo Dev.', e.message || 'Falha ao carregar preview do reset de pagamentos.');
      setPaymentsResetPreview(null);
    } finally {
      setPaymentsPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'dev') return;
    loadDevResetPreview();
    loadPaymentsResetPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (!Array.isArray(app.companies) || app.companies.length === 0) {
      setEndpointCompanyId('');
      return;
    }
    setEndpointCompanyId((current) => {
      if (current && app.companies.some((company) => String(company?.id) === current)) return current;
      return String(app.companies[0]?.id || '');
    });
  }, [app.companies]);

  if (!app.workspaceReady || loading) {
    return <SpWorkspaceLoading active="settings" title="A carregar configurações…" />;
  }

  const selectedEndpointCompany = (app.companies || []).find((company) => String(company?.id) === endpointCompanyId) || null;
  const selectedApiUrl = String(selectedEndpointCompany?.sienge_api_url || 'https://api.sienge.com.br').replace(/\/+$/, '');
  const selectedSubdomain = String(selectedEndpointCompany?.sienge_subdomain || '').trim();
  const selectedApiVersion = String(selectedEndpointCompany?.sienge_api_version || 'v1').trim() || 'v1';
  const selectedSiengeBase = selectedSubdomain
    ? `${selectedApiUrl}/${selectedSubdomain}/public/api/${selectedApiVersion}`
    : `${selectedApiUrl}/[subdomain]/public/api/${selectedApiVersion}`;
  const settingsEndpoints = [
    { method: 'GET', path: '/creditors?typesId=FU&limit=100&offset=0', note: 'Consulta de credores' },
    { method: 'GET', path: '/companies?limit=100&offset=0', note: 'Consulta de companias' },
    { method: 'GET', path: '/cost-centers?limit=100&offset=0', note: 'Consulta de centros de custo' },
    { method: 'GET', path: '/indexers', note: 'Consulta aos indexadores' },
    { method: 'GET', path: '/document-identifications', note: 'Consulta de Identificadores de Documentos' },
    { method: 'GET', path: '/payment-categories', note: 'Consulta de Categorias de Pagamentos.\n\nIMPORTANTE - O link acima não exibe algumas categorias de pagamento filhas\n/public/api/v1/payment-categories/20102 - Contém: 2010201, 2010202, 2010203 ....\n/public/api/v1/payment-categories/20103 - Contém: 2010301, 2010302, 2010303 ....' },
    { method: 'GET', path: '/bills?startDate=2026-04-01&endDate=2026-05-01', note: 'Consulta de Títulos a Pagar\n\nOBS: Restringindo por Credor \n/public/api/v1/bills?startDate=2026-04-01&endDate=2026-05-01&creditorId=26' },
  ];

  return (
    <PShell active="settings">
      <div className="sp-screen-head sp-screen-head--settings">
        <div className="sp-screen-head__block">
          <div className="sp-caps sp-screen-head__caps">Sienge Pay</div>
          <h1 className="sp-screen-head__title">Configurações</h1>
          <div className="sp-screen-head__meta">
            Parâmetros globais de funcionamento. A sincronização total de credores usa estes valores em tempo real.
          </div>
        </div>
        <div className="sp-settings-head-actions" role="toolbar" aria-label="Atalhos">
          <button type="button" className="sp-btn sp-btn-outline sp-btn-tiny" onClick={() => app.navigate('profile')}>
            <Icon name="user" size={14} /> Perfil
          </button>
          <button type="button" className="sp-btn sp-btn-outline sp-btn-tiny" onClick={() => app.navigate('dashboard')}>
            <Icon name="grid" size={14} /> Dashboard
          </button>
        </div>
      </div>

      {err && <div className="sp-creditors-banner sp-creditors-banner--err sp-settings-banner">{err}</div>}

      {!err && (
        <>
          <div className="sp-settings-tabs" role="tablist" aria-label="Configurações">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'api'}
              className={`sp-btn sp-btn-outline sp-btn-tiny${activeTab === 'api' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('api')}
            >
              API Sienge
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'sienge-fields'}
              className={`sp-btn sp-btn-outline sp-btn-tiny${activeTab === 'sienge-fields' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('sienge-fields')}
            >
              Campos do Sienge
            </button>
            {isDevModeAllowed && (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'layout'}
                className={`sp-btn sp-btn-outline sp-btn-tiny${activeTab === 'layout' ? ' is-active' : ''}`}
                onClick={() => setActiveTab('layout')}
              >
                Layout
              </button>
            )}
            {isDevModeAllowed && (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'dev'}
                className={`sp-btn sp-btn-outline sp-btn-tiny${activeTab === 'dev' ? ' is-active' : ''}`}
                onClick={() => setActiveTab('dev')}
              >
                Modo Dev.
              </button>
            )}
            {isDevModeAllowed && (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'backend'}
                className={`sp-btn sp-btn-outline sp-btn-tiny${activeTab === 'backend' ? ' is-active' : ''}`}
                onClick={() => setActiveTab('backend')}
              >
                Backend
              </button>
            )}
            {isDevModeAllowed && (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'debug'}
                className={`sp-btn sp-btn-outline sp-btn-tiny${activeTab === 'debug' ? ' is-active' : ''}`}
                onClick={() => setActiveTab('debug')}
              >
                DEBUG
              </button>
            )}
          </div>

          {activeTab === 'api' && form && (
            <div className="sp-settings-api-grid">
              <div className="sp-settings-api-column">
                <div className="sp-card sp-settings-card">
                  <div className="sp-settings-card__title">Parâmetros de sincronização massiva com a API Sienge</div>
                  <p className="sp-settings-lead">
                    Valores usados para gerenciar volume de dados por requisição.
                    {!canEdit && ' A sua conta não pode alterar estes campos.'}
                  </p>

                  <div className="sp-company-form-grid sp-settings-grid">
                    {KEYS.map(({ k, label, hint, min, max, step }) => (
                      <div
                        key={k}
                        className={`sp-settings-field${k === 'max_pages_per_task' ? ' sp-settings-field--offsets' : ''}`}
                      >
                        <label className="sp-caps sp-label" htmlFor={`sp-set-${k}`}>
                          {label}
                        </label>
                        <input
                          id={`sp-set-${k}`}
                          type="number"
                          className="sp-input"
                          disabled={!canEdit || saving}
                          min={min}
                          max={max}
                          step={step}
                          value={form[k] === '' || form[k] === undefined ? '' : form[k]}
                          onChange={(e) => onChange(k, e.target.value)}
                        />
                        <div className="sp-settings-hint">{hint}</div>
                        {fileDefaults && (
                          <div className="sp-settings-default sp-mono">
                            Default ficheiro: {k === 'max_pages_per_task' ? '5' : String(fileDefaults[k])}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {canEdit && (
                    <div className="sp-settings-actions">
                      <button type="button" className="sp-btn sp-btn-accent" disabled={saving} onClick={save}>
                        {saving ? 'A gravar…' : 'Guardar alterações'}
                      </button>
                      <button type="button" className="sp-btn sp-btn-outline" disabled={saving || !stored} onClick={resetDefaults}>
                        Repor defaults do servidor
                      </button>
                    </div>
                  )}
                </div>

                <div className="sp-card sp-settings-card">
                  <div className="sp-settings-card__title">Parâmetro de sincronização de preferências dos credores</div>
                  <p className="sp-settings-lead">Sincroniza CompanhiaID e CentroDeCustoID dos credores.</p>
                  <div className="sp-company-form-grid sp-settings-grid">
                    <div className="sp-settings-field">
                      <label className="sp-caps sp-label" htmlFor="sp-set-pref-batch">Número de credores por tarefa</label>
                      <input
                        id="sp-set-pref-batch"
                        type="number"
                        className="sp-input"
                        min={1}
                        max={20}
                        step={1}
                        value={prefSyncForm.batch_size}
                        onChange={(e) => setPrefSyncForm((f) => ({ ...f, batch_size: e.target.value === '' ? '' : Number(e.target.value) }))}
                        disabled={prefSyncSaving}
                      />
                      <div className="sp-settings-hint">1–20. Quantos credores são consultados na API Sienge por execução de cron.</div>
                      <div className="sp-settings-default sp-mono">Default: 10</div>
                    </div>
                    <div className="sp-settings-field">
                      <label className="sp-caps sp-label" htmlFor="sp-set-pref-filter">Filtro de sincronização</label>
                      <select
                        id="sp-set-pref-filter"
                        className="sp-input"
                        value={prefSyncForm.filter}
                        onChange={(e) => setPrefSyncForm((f) => ({ ...f, filter: e.target.value }))}
                        disabled={prefSyncSaving}
                      >
                        <option value="all">Todos os Credores</option>
                        <option value="employees">Somente Funcionários</option>
                      </select>
                      <div className="sp-settings-hint">Define quais credores ativos serão incluídos nas tarefas de sincronização.</div>
                    </div>
                  </div>
                  <div className="sp-settings-actions">
                    <button type="button" className="sp-btn sp-btn-accent" disabled={prefSyncSaving} onClick={savePrefSync}>
                      {prefSyncSaving ? 'A gravar…' : 'Guardar alterações'}
                    </button>
                    <button type="button" className="sp-btn sp-btn-outline" disabled={prefSyncSaving} onClick={() => {
                      setPrefSyncForm({ batch_size: 10, filter: 'employees' });
                      app.updateCreditorPrefSync({ batch_size: 10 })
                        .then(() => app.toast('success', 'Preferências', 'Reposto para o default (10).'))
                        .catch((e) => app.toast('error', 'Preferências', e.message || 'Falha ao repor.'));
                    }}>
                      Repor defaults do servidor
                    </button>
                  </div>
                </div>

                <div className="sp-card sp-settings-card">
                  <div className="sp-settings-card__title">Documentações da API</div>
                  <div className="sp-settings-endpoints-list">
                    <div className="sp-settings-endpoint-row">
                      <div className="sp-chip">DOC</div>
                      <div className="sp-mono">
                        <a href="https://api.sienge.com.br/docs/" target="_blank" rel="noreferrer">
                          https://api.sienge.com.br/docs/
                        </a>
                      </div>
                    </div>
                    <div className="sp-settings-endpoint-row">
                      <div className="sp-chip">DOC</div>
                      <div className="sp-mono">
                        <a href="https://api.sienge.com.br/v1/docs/" target="_blank" rel="noreferrer">
                          https://api.sienge.com.br/v1/docs/
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sp-card sp-settings-card sp-settings-api-endpoints">
                <div className="sp-settings-card__title">Endpoints</div>
                <div className="sp-settings-endpoints-pick">
                  <label className="sp-caps sp-label" htmlFor="sp-settings-endpoint-company">Escolha uma empresa:</label>
                  <select
                    id="sp-settings-endpoint-company"
                    className="sp-input"
                    value={endpointCompanyId}
                    onChange={(e) => setEndpointCompanyId(e.target.value)}
                  >
                    {(app.companies || []).map((company) => (
                      <option key={company.id} value={String(company.id)}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sp-settings-endpoints-list">
                  {settingsEndpoints.map((ep) => (
                    <div key={`${ep.method}-${ep.path}`} className="sp-settings-endpoint-row">
                      <a
                        className="sp-chip"
                        href={`${selectedSiengeBase}${ep.path}`}
                        target="_blank"
                        rel="noreferrer"
                        title={`Abrir endpoint ${ep.method}`}
                      >
                        {ep.method}
                      </a>
                      <div className="sp-mono">
                        <a href={`${selectedSiengeBase}${ep.path}`} target="_blank" rel="noreferrer">
                          {selectedSiengeBase}{ep.path}
                        </a>
                      </div>
                      <div className="sp-settings-hint">
                        {String(ep.note || '').split('\n').map((line, idx, arr) => {
                          const trimmed = String(line || '').trim();
                          const isImportantLine = trimmed.startsWith('IMPORTANTE');
                          return (
                            <React.Fragment key={`${ep.method}-${ep.path}-note-${idx}`}>
                              {isImportantLine ? (
                                <span className="sp-settings-hint--important">
                                  <strong>IMPORTANTE</strong>
                                  {trimmed.slice('IMPORTANTE'.length)}
                                </span>
                              ) : (
                                line
                              )}
                              {idx < arr.length - 1 ? <br /> : null}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {isDevModeAllowed && activeTab === 'layout' && (
            <>
              <div className="sp-card sp-settings-card">
                <div className="sp-settings-card__title">Layout — Tela de Login</div>
                <p className="sp-settings-lead">Personaliza os textos exibidos na tela de login. Visível apenas para o usuário ID 1.</p>
                <div className="sp-company-form-grid sp-settings-grid">
                  {LOGIN_LAYOUT_FIELDS.map(({ key, label, hint, textarea }) => (
                    <div key={key} className="sp-settings-field">
                      <label className="sp-caps sp-label" htmlFor={`sp-layout-${key}`}>{label}</label>
                      {textarea ? (
                        <textarea
                          id={`sp-layout-${key}`}
                          className="sp-input"
                          rows={3}
                          style={{ resize: 'vertical', fontFamily: 'inherit' }}
                          value={layoutForm[key] || ''}
                          onChange={(e) => setLayoutForm((f) => ({ ...f, [key]: e.target.value }))}
                          disabled={layoutSaving}
                        />
                      ) : (
                        <input
                          id={`sp-layout-${key}`}
                          type="text"
                          className="sp-input"
                          value={layoutForm[key] || ''}
                          onChange={(e) => setLayoutForm((f) => ({ ...f, [key]: e.target.value }))}
                          disabled={layoutSaving}
                        />
                      )}
                      <div className="sp-settings-hint">{hint}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="sp-card sp-settings-card" style={{ marginTop: '1.5rem' }}>
                <div className="sp-settings-card__title">Layout — Menu Lateral</div>
                <p className="sp-settings-lead">Personaliza os textos exibidos no aside do menu lateral.</p>
                <div className="sp-company-form-grid sp-settings-grid">
                  {MENU_LAYOUT_FIELDS.map(({ key, label, hint }) => (
                    <div key={key} className="sp-settings-field">
                      <label className="sp-caps sp-label" htmlFor={`sp-layout-${key}`}>{label}</label>
                      <input
                        id={`sp-layout-${key}`}
                        type="text"
                        className="sp-input"
                        value={layoutForm[key] || ''}
                        onChange={(e) => setLayoutForm((f) => ({ ...f, [key]: e.target.value }))}
                        disabled={layoutSaving}
                      />
                      <div className="sp-settings-hint">{hint}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="sp-settings-actions" style={{ marginTop: '1.5rem' }}>
                <button type="button" className="sp-btn sp-btn-accent" disabled={layoutSaving} onClick={saveLayout}>
                  {layoutSaving ? 'A gravar…' : 'Guardar layout'}
                </button>
              </div>
            </>
          )}

          {isDevModeAllowed && activeTab === 'debug' && (
            <div className="sp-card sp-settings-card">
              <div className="sp-settings-card__title">DEBUG</div>
              <p className="sp-settings-lead">Controla logs e comportamentos de diagnóstico no frontend.</p>

              <div className="sp-settings-field" style={{ maxWidth: 280 }}>
                <label className="sp-caps sp-label" htmlFor="sp-set-debug-enabled">Ativar Debug</label>
                <select
                  id="sp-set-debug-enabled"
                  className="sp-input"
                  value={app.debugEnabled || 'N'}
                  onChange={(e) => app.setDebugEnabled(e.target.value)}
                >
                  <option value="S">Sim</option>
                  <option value="N">Não</option>
                </select>
                <div className="sp-settings-hint">Preferência gravada no backend por usuário.</div>
              </div>

              <div className="sp-settings-actions">
                <button type="button" className="sp-btn sp-btn-accent" onClick={saveDebug}>
                  Guardar DEBUG
                </button>
              </div>
            </div>
          )}

          {isDevModeAllowed && activeTab === 'backend' && (
            <div className="sp-card sp-settings-card">
              <div className="sp-settings-card__title">Backend</div>
              <p className="sp-settings-lead">Configuração fixa do backend usada pelo frontend (apenas visualização).</p>

              <div className="sp-company-form-grid sp-settings-grid">
                <div className="sp-settings-field">
                  <label className="sp-caps sp-label" htmlFor="sp-set-backend-baseurl">baseUrl / host</label>
                  <input
                    id="sp-set-backend-baseurl"
                    type="text"
                    className="sp-input"
                    value={app.debugBackendConfig?.baseUrl || ''}
                    disabled
                    readOnly
                  />
                </div>

                <div className="sp-settings-field">
                  <div className="sp-settings-hint">
                    Documentação dinâmica da API:{' '}
                    <a
                      href={`${String(app.debugBackendConfig?.baseUrl || '').replace(/\/+$/, '')}${String(app.debugBackendConfig?.docsPath || '/docs/api').startsWith('/') ? '' : '/'}${String(app.debugBackendConfig?.docsPath || '/docs/api')}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Abrir documentação
                    </a>
                  </div>
                  <div className="sp-settings-hint">Restrição de IP do Backend em: backend/.env</div>
                  <div className="sp-settings-hint sp-mono">API_IP_ALLOWLIST_ENABLED=true</div>
                  <div className="sp-settings-hint sp-mono">API_ALLOWED_IPS=127.0.0.1,::1 [para liberação de backend e frontend na mesma máquina]</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sienge-fields' && (
            <div className="sp-card sp-settings-card">
              <div className="sp-settings-card__title">Campos do Sienge</div>
              <p className="sp-settings-lead">
                Gestão de valores de apoio usados na integração. Cada opção possui <span className="sp-mono">valor</span> e <span className="sp-mono">nome</span>.
                {!canEdit && ' A sua conta não pode alterar estes campos.'}
              </p>
              <div className="sp-sienge-fields-grid">
                {SIENGE_FIELD_GROUPS.map((group) => (
                  <div key={group.key} className="sp-sienge-fields-card">
                    <div className="sp-sienge-fields-card__head">
                      <div className="sp-sienge-fields-card__title">{group.label}</div>
                      {group.title && <div className="sp-sienge-fields-card__meta">{group.title}</div>}
                      <div className="sp-sienge-fields-card__meta">
                        {fieldsStored ? 'Origem: base de dados' : 'Origem: ficheiro padrão'}
                        {fieldsDbDefaults && (
                          <span className="sp-mono"> · default (base): {Array.isArray(fieldsDbDefaults[group.key]) ? fieldsDbDefaults[group.key].length : 0} itens</span>
                        )}
                      </div>
                    </div>
                    <div className="sp-sienge-fields-list">
                      {(fieldsForm[group.key] || []).map((row, idx) => (
                        <div className="sp-sienge-fields-row" key={`${group.key}-${idx}`}>
                          <input
                            type="text"
                            className="sp-input sp-sienge-fields-row__value"
                            placeholder="valor"
                            value={row?.value || ''}
                            onChange={(e) => updateFieldRow(group.key, idx, { value: e.target.value })}
                            disabled={!canEdit || saving}
                          />
                          <input
                            type="text"
                            className="sp-input sp-sienge-fields-row__name"
                            placeholder="nome"
                            value={row?.name || ''}
                            onChange={(e) => updateFieldRow(group.key, idx, { name: e.target.value })}
                            disabled={!canEdit || saving}
                          />
                          {canEdit && (
                            <button type="button" className="sp-btn sp-btn-outline sp-btn-tiny" onClick={() => removeFieldRow(group.key, idx)} disabled={saving}>
                              Excluir
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    {canEdit && (
                      <button type="button" className="sp-btn sp-btn-outline sp-btn-tiny" onClick={() => addFieldRow(group.key)} disabled={saving}>
                        <Icon name="plus" size={13} /> Adicionar opção
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="sp-sienge-fixed-fields-card">
                <div className="sp-sienge-fields-card__head">
                  <div className="sp-sienge-fields-card__title">Campos fixos</div>
                </div>
                <div className="sp-sienge-fixed-fields-grid">
                  {SIENGE_FIXED_FIELDS.map((field) => (
                    <div key={field.key} className="sp-settings-field">
                      <label className="sp-caps sp-label" htmlFor={`sp-fixed-${field.key}`}>{field.label}</label>
                      <input
                        id={`sp-fixed-${field.key}`}
                        type="text"
                        className="sp-input"
                        step={field.kind === 'decimal' ? '0.1' : '1'}
                        inputMode={field.kind === 'decimal' ? 'decimal' : field.kind === 'costCode' ? 'numeric' : undefined}
                        value={field.kind === 'costCode'
                          ? formatCostCode(fieldsForm?.fixed_fields?.[field.key])
                          : (fieldsForm?.fixed_fields?.[field.key] ?? '')}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const next = field.kind === 'costCode'
                            ? raw.replace(/\D/g, '')
                            : field.kind === 'decimal'
                              ? raw.replace(',', '.')
                              : (raw === '' ? '' : Number(raw));
                          setFieldsForm((curr) => ({
                            ...curr,
                            fixed_fields: {
                              ...(curr.fixed_fields || {}),
                              [field.key]: next,
                            },
                          }));
                        }}
                        onBlur={() => {
                          if (field.kind === 'costCode') {
                            const raw = fieldsForm?.fixed_fields?.[field.key];
                            setFieldsForm((curr) => ({
                              ...curr,
                              fixed_fields: {
                                ...(curr.fixed_fields || {}),
                                [field.key]: String(raw ?? '').replace(/\D/g, ''),
                              },
                            }));
                          } else if (field.kind === 'decimal') {
                            setFieldsForm((curr) => ({
                              ...curr,
                              fixed_fields: {
                                ...(curr.fixed_fields || {}),
                                [field.key]: formatDecimalOne(curr?.fixed_fields?.[field.key]),
                              },
                            }));
                          }
                        }}
                        disabled={!canEdit || saving}
                      />
                      <div className="sp-settings-hint">{field.subtitle}</div>
                    </div>
                  ))}
                </div>
              </div>
              {canEdit && (
                <div className="sp-settings-actions">
                  <button type="button" className="sp-btn sp-btn-accent" disabled={saving} onClick={saveSiengeFields}>
                    {saving ? 'A gravar…' : 'Guardar campos do Sienge'}
                  </button>
                  <button type="button" className="sp-btn sp-btn-outline" disabled={saving} onClick={resetSiengeFields}>
                    Repor defaults da base
                  </button>
                </div>
              )}
            </div>
          )}

          {isDevModeAllowed && activeTab === 'dev' && (
            <>
              <div className="sp-card sp-settings-card">
                <div className="sp-settings-card__title">Reset os Pagamentos</div>
                <p className="sp-settings-lead">
                  Apaga todos os pagamentos, lotes, aprovações e logs de transmissão.
                  Empresas e credores são preservados.
                </p>
                <div className="sp-settings-field">
                  <label className="sp-caps sp-label">Preview (modo seco)</label>
                  {paymentsPreviewLoading ? (
                    <div className="sp-settings-hint">A carregar contagem de registos...</div>
                  ) : (
                    <div className="sp-settings-default sp-mono">
                      {paymentsResetPreview
                        ? Object.entries(paymentsResetPreview).map(([k, v]) => `${k}: ${v}`).join(' · ')
                        : 'Sem dados de preview.'}
                    </div>
                  )}
                </div>
                <div className="sp-settings-actions">
                  <button type="button" className="sp-btn sp-btn-outline" disabled={paymentsResetSaving || paymentsPreviewLoading} onClick={loadPaymentsResetPreview}>
                    Atualizar preview
                  </button>
                  <button type="button" className="sp-btn sp-btn-outline" disabled={!canEdit || paymentsResetSaving} onClick={() => openResetModal('payments')}>
                    {paymentsResetSaving ? 'A limpar…' : 'Reset os Pagamentos'}
                  </button>
                </div>
              </div>

              <div className="sp-card sp-settings-card" style={{ marginTop: '1.5rem' }}>
                <div className="sp-settings-card__title">Reset o Sistema</div>
                <p className="sp-settings-lead">
                  Ferramenta de limpeza total para ambiente de desenvolvimento.
                  Remove empresas, credores, logs de sincronização e pagamentos com todos os registos associados.
                </p>
                <div className="sp-settings-field">
                  <label className="sp-caps sp-label">Preview (modo seco)</label>
                  {devPreviewLoading ? (
                    <div className="sp-settings-hint">A carregar contagem de registos...</div>
                  ) : (
                    <div className="sp-settings-default sp-mono">
                      {devResetPreview
                        ? Object.entries(devResetPreview).map(([k, v]) => `${k}: ${v}`).join(' · ')
                        : 'Sem dados de preview.'}
                    </div>
                  )}
                </div>
                <div className="sp-settings-actions">
                  <button type="button" className="sp-btn sp-btn-outline" disabled={saving || devPreviewLoading} onClick={loadDevResetPreview}>
                    Atualizar preview
                  </button>
                  <button type="button" className="sp-btn sp-btn-outline" disabled={!canEdit || saving} onClick={() => openResetModal('system')}>
                    {saving ? 'A limpar…' : 'Reset o Sistema'}
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {resetModalOpen && (
        <div className="sp-overlay" onClick={() => setResetModalOpen(false)}>
          <div className="sp-modal sp-modal--sm" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal__header">
              <h3 className="sp-modal__title">
                {resetModalType === 'payments' ? 'Reset os Pagamentos' : 'Reset o Sistema'}
              </h3>
            </div>
            <div className="sp-modal__body">
              <p className="sp-settings-lead" style={{ marginBottom: '1rem' }}>
                {resetModalType === 'payments'
                  ? 'Isto apagará todos os pagamentos, lotes, aprovações e logs de transmissão. Empresas e credores serão preservados.'
                  : 'Isto apagará empresas, credores, logs e pagamentos com todos os registos associados.'}
              </p>
              <p className="sp-settings-lead" style={{ marginBottom: '0.75rem', fontWeight: 600 }}>
                Confirme sua senha para prosseguir:
              </p>
              <input
                type="password"
                className="sp-input"
                placeholder="Senha do usuário ID 1"
                value={resetModalPassword}
                onChange={(e) => { setResetModalPassword(e.target.value); setResetModalError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') confirmReset(); }}
                autoFocus
              />
              {resetModalError && (
                <div className="sp-settings-hint" style={{ color: 'var(--sp-color-danger, #dc3545)', marginTop: '0.5rem' }}>
                  {resetModalError}
                </div>
              )}
            </div>
            <div className="sp-modal__footer" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', padding: '1rem' }}>
              <button type="button" className="sp-btn sp-btn-outline" onClick={() => setResetModalOpen(false)}>
                Cancelar
              </button>
              <button type="button" className="sp-btn sp-btn-accent" onClick={confirmReset}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel={confirmModal?.confirmLabel}
        onConfirm={() => {
          confirmModal?.onConfirm?.();
          setConfirmModal(null);
        }}
        onCancel={() => setConfirmModal(null)}
      />
    </PShell>
  );
}
